import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";
import { Lock as LockIcon } from "@mui/icons-material";
import { registerClaveSegura } from "./claveSeguraService";

const vacio = { clave: "", clave2: "", password_login: "" };

// Diálogo global que pide (o crea, la primera vez) la clave de seguridad
// cuando una ruta protegida con 'clave.segura' responde 423.
const ClaveSeguraDialog = () => {
  const [open, setOpen] = useState(false);
  const [crear, setCrear] = useState(false);
  const [form, setForm] = useState(vacio);
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [ambito, setAmbito] = useState("global");
  const resolver = useRef(null);
  const primerCampo = useRef(null);

  // autoFocus no basta: el Dialog de MUI mueve el foco al contenedor al abrir.
  const enfocar = () => setTimeout(() => primerCampo.current?.focus(), 0);

  const pedir = useCallback(({ configurada = true, ambito = "global" } = {}) => {
    setAmbito(ambito);
    setCrear(!configurada);
    setForm(vacio);
    setError("");
    setOpen(true);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  useEffect(() => {
    registerClaveSegura(pedir);
  }, [pedir]);

  const cerrar = (ok) => {
    setOpen(false);
    resolver.current?.(ok);
    resolver.current = null;
  };

  const enviar = async (e) => {
    e?.preventDefault();
    setError("");

    if (crear) {
      if (form.clave.length < 8) return setError("La clave debe tener al menos 8 caracteres.");
      if (form.clave !== form.clave2) return setError("Las claves no coinciden.");
    }

    setEnviando(true);
    try {
      await axios({
        url: `${process.env.REACT_APP_API_URL}${crear ? "/claveSegura" : "/claveSegura/verificar"}`,
        method: "POST",
        data: crear
          ? { password_login: form.password_login, clave: form.clave, ambito }
          : { clave: form.clave, ambito },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      cerrar(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "CLAVE_SEGURA_NO_CONFIGURADA") {
        setCrear(true);
        setForm(vacio);
        setError("No tienes clave de seguridad (o se ha reseteado). Crea una nueva.");
      } else {
        setError(data?.message || "No se pudo conectar con el servidor.");
        setForm((f) => ({ ...f, clave: "", clave2: "" }));
      }
      enfocar();
    } finally {
      setEnviando(false);
    }
  };

  const campo = (key, label, primero = false) => (
    <TextField
      label={label}
      type="password"
      size="small"
      fullWidth
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      inputProps={{ autoComplete: "new-password" }}
      inputRef={primero ? primerCampo : undefined}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={() => !enviando && cerrar(false)}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEntered: enfocar }}
    >
      <form onSubmit={enviar}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockIcon color="warning" />
          {crear ? "Crear clave de seguridad" : "Clave de seguridad"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {crear
                ? "Para ver datos sensibles necesitas una segunda contraseña, distinta de la de acceso. Confirma con tu contraseña de acceso y elige la nueva clave."
                : "Estos datos son sensibles. Introduce tu clave de seguridad (no la de acceso). Solo se desbloquea este elemento, durante unos minutos."}
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {crear && campo("password_login", "Contraseña de acceso", true)}
            {campo("clave", crear ? "Nueva clave de seguridad" : "Clave de seguridad", !crear)}
            {crear && campo("clave2", "Repetir clave de seguridad")}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => cerrar(false)} disabled={enviando}>Cancelar</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={enviando || !form.clave || (crear && !form.password_login)}
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {crear ? "Crear y desbloquear" : "Desbloquear"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClaveSeguraDialog;
